import { useEffect, useState } from 'react';
import TableCompo from '../Table/TableCompo';

import {
  Box,
  Typography,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  TextareaAutosize,
  FormGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CiEdit } from 'react-icons/ci';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import {
  getConfiguration,
  getAppConfig,
} from '../../../redux/Configuration/configurationSlice';
import { canShowPermission } from '../Permissions/permissionObject';
import {
  fetchSipPlansRequest,
  deleteSipPlanRequest,
  changeSipStatusRequest,
  setCurrentSipPlan,
  createSipPlanRequest,
  resetSomethingState,
} from '../../../redux/SipPlan/SipPlanSlice';
import { toast } from 'react-toastify';
import { getMetalsRequest } from '../../../redux/GetMetals/metalSlice';
import AddIcon from '@mui/icons-material/Add';
import { minWidth } from '@mui/system';
import LuckySpinModal from './LuckySpinModal';
import useCsvConverter from '../../../hooks/convertToCSV';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import { controlAllFeatureOptions } from '../../../configs/constants';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';

const getMultiWinnerHelperText = (noOfWinners) => {
  const count = parseInt(String(noOfWinners), 10);
  if (!noOfWinners || Number.isNaN(count) || count < 2) {
    return 'Enter total winners (minimum 2).';
  }
  if (count === 2) {
    return '2 winners: position 1 (main) and position 2 (additional).';
  }
  return `${count} winners: position 1 (main), positions 2–${count} (${count - 1} additional).`;
};

const getEditPayloadPlanName = (name, autoGroupCreation) => {
  const currentName = String(name ?? '');
  if (!autoGroupCreation) return currentName;
  return currentName.replace(/\s*\[Group\s*\d+\]\s*$/i, '').trim();
};

const CreateSipPlanTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const configurationData = useSelector(
    (state) => state.appConfig.configurationData,
  );
  const getAppConfigData = useSelector((state) => state.appConfig.configData);
  const decimal = getAppConfigData?.decimal || 4;
  const {
    sipPlans = [],
    loading,
    success,
    error,
    total = 0,
  } = useSelector((state) => state.sipplan);
  const metalState = useSelector((state) => state.metal || {});
  const data = metalState.data || {};
  const enabledMetals = data.metals || [];
  const isOldAppVersion = configurationData?.appVersion === 'old'; 

  const [sipLuckyDrawStatus, setSipLuckyDrawStatus] = useState(false);
  // useEffect(() => {
  //   if (configurationData?.sipLuckyDraw) {
  //     setSipLuckyDrawStatus(configurationData.sipLuckyDraw === "true");
  //   }
  // }, [configurationData]);

  const [sipOfferType, setSipOfferType] = useState(false);
  const autoGroupSipFeatureKey = controlAllFeatureOptions.Auto_group_sip;
  const sipLuckyDrawFeatureKey = controlAllFeatureOptions.Sip_Luckey_Draw;
  const sipMultiWinnerLuckyDrawFeatureKey =
    controlAllFeatureOptions.Multi_Winner_Lucky_Draw;
  const autoDebitDateSelectionFeatureKey =
    controlAllFeatureOptions.Auto_Debit_Date_Selection;
  const autoDebitDateSelectionEnabled =
    getAppConfigData?.featureToggle?.[autoDebitDateSelectionFeatureKey] ===
      true ||
    getAppConfigData?.featureToggle?.auto_debit_date_selection === true;
  const sipAutoGroupCreationEnabled =
    getAppConfigData?.featureToggle?.[autoGroupSipFeatureKey] === true ||
    getAppConfigData?.featureToggle?.auto_group_sip === true;
  const sipMultiWinnerLuckyDrawEnabled =
    getAppConfigData?.featureToggle?.[sipMultiWinnerLuckyDrawFeatureKey] ===
      true || getAppConfigData?.featureToggle?.multi_winner_lucky_draw === true;
  useEffect(() => {
    if (Object.keys(getAppConfigData).length) {
      setSipOfferType(getAppConfigData?.featureToggle?.sip_offertype === true);
      setSipLuckyDrawStatus(
        getAppConfigData?.featureToggle?.[sipLuckyDrawFeatureKey] === true ||
          getAppConfigData?.featureToggle?.sip_luckey_draw === true,
      );
    }
  }, [getAppConfigData, sipLuckyDrawFeatureKey]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  // const [formData, setFormData] = useState({});

  // Status dialog
  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [selectedStatusPlan, setSelectedStatusPlan] = useState(null);

  // Delete dialog
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [selectedDeletePlan, setSelectedDeletePlan] = useState(null);

  //  Open dialog before status update
  const handleStatusChange = (planId, currentStatus) => {
    setSelectedStatusPlan({ planId, currentStatus });
    setConfirmStatusDialogOpen(true);
  };

  const paginatedData = Array.isArray(sipPlans)
    ? sipPlans.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    : [];

  const handleConfirmStatusYes = () => {
    if (selectedStatusPlan) {
      dispatch(
        changeSipStatusRequest({
          planid: selectedStatusPlan.planId,
          set_sip_status: !selectedStatusPlan.currentStatus,
        }),
      );
      toast.success('SIP plan status changed');
    }
    setConfirmStatusDialogOpen(false);
    setSelectedStatusPlan(null);
  };

  const handleConfirmStatusNo = () => {
    setConfirmStatusDialogOpen(false);
    setSelectedStatusPlan(null);
  };

  //  Open dialog before delete
  const handleDeleteClick = (planId) => {
    setSelectedDeletePlan(planId);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDeleteYes = () => {
    dispatch(deleteSipPlanRequest(selectedDeletePlan));
    setConfirmDeleteDialogOpen(false);
    setSelectedDeletePlan(null);
  };

  const handleConfirmDeleteNo = () => {
    setConfirmDeleteDialogOpen(false);
    setSelectedDeletePlan(null);
  };

  // const handleEditClick = (row) => {
  //   dispatch(setCurrentSipPlan(row));
  //   setFormData({
  //     ...row,
  //     offerType: row.offerType || "",
  //     percentage:
  //       row.offerType === "PREPAID_FIRST_INSTALLMENT" ? 0 : row.percentage || 0,
  //   });
  //   setOpenEditDialog(true);
  // };

  const handleEditClick = (row) => {
    dispatch(setCurrentSipPlan(row));

    let percentage = 0;
    let subsequent_percentage = 0;

    if (row.offerType === 'EVERY_INSTALLMENT_DISCOUNT') {
      percentage = row.percentage || 0;
    } else if (row.offerType === 'FIRST_AND_SUBSEQUENT') {
      percentage = row.percentage || 0;
      subsequent_percentage = row.subsequent_percentage || 0;
    }

    const hour24 = row.luckyDrawTime;
    const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const amPm = hour24 >= 12 ? 'PM' : 'AM';

    setFormData({
      ...row,
      offerType: row.offerType || '',
      isMoneyType: isOldAppVersion ? true : Boolean(row.isMoneyType),
      isWeightType: isOldAppVersion ? false : Boolean(row.isWeightType),
      minGram: row.minGram ?? '',
      maxGram: row.maxGram ?? '',
      percentage,
      subsequent_percentage,
      hour,
      amPm,
      enableLuckyDraw: row.enableLuckyDraw,
      multi_winner_lucky_draw: row.multi_winner_lucky_draw || false,
      no_of_winners: row.no_of_winners || 2,
      installmentDuration:
        row.installmentDuration != null && row.installmentDuration !== ''
          ? Number(row.installmentDuration)
          : '',
      ...(autoDebitDateSelectionEnabled && {
        auto_debit_date_selection: Boolean(row.auto_debit_date_selection),
      }),
    });

    setOpenEditDialog(true);
  };

  useEffect(() => {
    dispatch(getMetalsRequest());
  }, [dispatch]);

  const [pagination, setPagination] = useState({ page: 0, limit: 5 });
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(
      fetchSipPlansRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
      }),
    );
  }, [dispatch, pagination, search]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;
      case 'changeRowsPerPage':
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
  if (isOldAppVersion) {
    setFormData((prev) => ({
      ...prev,
      isMoneyType: true,
      isWeightType: false,
    }));
  }
}, [isOldAppVersion]);

  const [formData, setFormData] = useState({
    planName: '',
    bullionId: '',
    minDuration: configurationData?.minDuration || '',
    maxDuration: configurationData?.maxDuration || '',
    minQty: configurationData?.minQty || '',
    maxQty: configurationData?.maxQty || '',
    minGram: configurationData?.mingram || '',
    maxGram: configurationData?.maxgram || '',
    isMoneyType: false,
    isWeightType: false,
    maxSipLimit: '',
    benefits: '',
    disclaimer: '',
    percentage: '',
    subsequent_percentage: '',
    offerType: '',
    interval: '',
    autodebit: '',
    auto_debit_date_selection: false,
    luckyDrawInterval: null,
    luckyDrawDate: null,
    luckyDrawTime: null,
    hour: '',
    amPm: '',
    autoGroupCreation: false,
    maxSipGroup: '',
    multi_winner_lucky_draw: false,
    no_of_winners: '',
    installmentDuration: '',
  });

  const [benefitsList, setBenefitsList] = useState([]);
  const [role, setRole] = useState('');
  const [open, setOpen] = useState(false);
  const [alertData, setAlertData] = useState({ type: '', message: '' });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSpinModalOpen, setIsSpinModalOpen] = useState(false);
  const [selectedSipPlan, setSelectedSipPlan] = useState(null);
  // useEffect(() => {
  //   dispatch(getMetalsRequest());
  // }, [dispatch]);
  useEffect(() => {
    if (configurationData) {
      setFormData((prev) => ({
        ...prev,
        minDuration: configurationData?.moneysip?.minmonth || '',
        maxDuration: configurationData?.moneysip?.maxmonth || '',
        minQty: configurationData?.moneysip?.minamount || '',
        maxQty: configurationData?.moneysip?.maxamount || '',
        minGram: configurationData?.moneysip?.mingram || '',
        maxGram: configurationData?.moneysip?.maxgram || '',
      }));
    }
  }, [configurationData]);
  const handleTypeChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  useEffect(() => {
    if (configurationData) {
      console.log('Configuration Data:', configurationData, getAppConfigData);
    }
  }, [configurationData]);
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'offerType') {
      setFormData((prev) => ({
        ...prev,
        offerType: value,
        percentage: value === 'Normal' ? 0 : prev.percentage,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value, // ✅ works for both inputs & checkboxes
      }));
    }
  };
  const handleAddBenefit = () => {
    if (formData.benefits !== '') {
      setBenefitsList([...benefitsList, formData.benefits]);
      setFormData({ ...formData, benefits: '' });
    }
  };

  // useEffect(() => {
  //   dispatch(getConfiguration());
  //   // dispatch(getAppConfig());
  // }, []);

  // Get max month from configuration
  const maxMonth = Number(configurationData?.moneysip?.maxmonth || 0);
  console.log('mmm', maxMonth);
  // Generate dropdown values from 1 → maxMonth
  const monthOptions = Array.from({ length: maxMonth }, (_, i) => i + 1);
  const installmentDurationOptions = Array.from({ length: 29 }, (_, i) => i);
  console.log('monthOptions', monthOptions);
  const minAmount = Number(configurationData?.moneysip?.minamount || 0);
  console.log('mmmaaaaaaaw', minAmount);
  const maxAmount = Number(configurationData?.moneysip?.maxamount || 0);
  console.log('mmmaaaaa', maxAmount);

  //sip spin
  function handleLuckySpinClick(plan) {
    setSelectedSipPlan(plan);
    setIsSpinModalOpen(true);
  }
  // Handles closing the modal
  const handleCloseModal = () => {
    setIsSpinModalOpen(false);
    setSelectedSipPlan(null);
    // Optionally dispatch a cleanup action to clear customerIds from Redux state
  };

  const handleSubmit = () => {
    const {
      planName,
      minDuration,
      maxDuration,
      role,
      minQty,
      maxQty,
      minGram,
      maxGram,
      maxSipLimit,
      percentage,
      // subsequent_percentage,
      offerType,
      // interval,
      autodebit,
      luckyDrawInterval,
      luckyDrawDate,
      luckyDrawTime,
    } = formData;

    const minAmount = Number(configurationData?.moneysip?.minamount || 0);
    const maxAmount = Number(configurationData?.moneysip?.maxamount || 0);
    const minGramLimit = Number(configurationData?.moneysip?.mingram || 0);
    const maxGramLimit = Number(configurationData?.moneysip?.maxgram || 0);

    if (!planName) return toast.error('Plan Name is required');
    // if (!planName) return toast.error("Plan Name is required");

    if (!offerType) return toast.error('offerType  is required');
    // if (!subsequent_percentage) return toast.error("subsequent_percentage  is required");
    if (!maxSipLimit) return toast.error('Max customer limit is required');

    if (!role) return toast.error('Metal is required');
    if (!minDuration) return toast.error('Minimum duration is required');
    if (!maxDuration) return toast.error('Maximum Duration is required');
    if (Number(minDuration) > Number(maxDuration)) {
      return toast.error('Max Duration cannot be less than Min Duration');
    }
    // Amount validation
    // if (!minQty) return toast.error('Minimum Quantity is required');
    // if (!maxQty) return toast.error('Maximum Quantity is required');
    if (!formData.isMoneyType && !formData.isWeightType) {
      return toast.error('Please select at least one type: Money or Weight');
    }
    if (formData.isMoneyType) {
      if (!minQty) return toast.error('Minimum Quantity is required');
      if (!maxQty) return toast.error('Maximum Quantity is required');
      if (Number(minQty) > Number(maxQty)) {
        return toast.error('Max Quantity cannot be less than Min amount');
      }
      if (Number(minQty) < minAmount) {
        return toast.error(`Min Quantity cannot be less than ${minAmount}`);
      }
      if (Number(maxQty) > maxAmount) {
        return toast.error(`Max Quantity cannot be greater than ${maxAmount}`);
      }
    }
    if (formData.isWeightType) {
      if (!minGram) return toast.error('Minimum Gram is required');
      if (!maxGram) return toast.error('Maximum Gram is required');
      if (Number(minGram) > Number(maxGram)) {
        return toast.error('Max Gram cannot be less than Min Gram');
      }
      if (minGramLimit && Number(minGram) < minGramLimit) {
        return toast.error(`Min Gram cannot be less than ${minGramLimit}`);
      }
      if (maxGramLimit && Number(maxGram) > maxGramLimit) {
        return toast.error(`Max Gram cannot be greater than ${maxGramLimit}`);
      }
    }

    //  Extra validation with configData
    if (Number(minQty) < minAmount) {
      return toast.error(`Min Quantity cannot be less than ${minAmount}`);
    }

    if (Number(maxQty) > maxAmount) {
      return toast.error(`Max Quantity cannot be greater than ${maxAmount}`);
    }

    //  Auto Group Creation validation
    if (sipAutoGroupCreationEnabled && formData.autoGroupCreation) {
      if (formData.maxSipGroup === '' || formData.maxSipGroup == null) {
        return toast.error('Max SIP group creation limit is required');
      }

      const parsedMaxSipGroup = parseInt(formData.maxSipGroup, 10);
      if (Number.isNaN(parsedMaxSipGroup)) {
        return toast.error(
          'Max SIP group creation limit must be a valid number',
        );
      }

      if (parsedMaxSipGroup < 0) {
        return toast.error(
          'Max SIP group creation limit cannot be less than 0',
        );
      }

      const configuredMaxSipGroupCreationLimit = Number(
        configurationData?.moneysip?.maxSipGroupCreationLimit ?? 0,
      );
      if (
        configuredMaxSipGroupCreationLimit > 0 &&
        parsedMaxSipGroup > configuredMaxSipGroupCreationLimit
      ) {
        return toast.error(
          `Max SIP group creation limit cannot be greater than ${configuredMaxSipGroupCreationLimit}`,
        );
      }
    }

    if (sipMultiWinnerLuckyDrawEnabled && formData.multi_winner_lucky_draw) {
      const winnersCount = parseInt(formData.no_of_winners, 10);
      if (!formData.no_of_winners || Number.isNaN(winnersCount)) {
        return toast.error('Number of winners per draw is required');
      }
      if (winnersCount < 2) {
        return toast.error('Number of winners must be at least 2');
      }
    }

    setHasSubmitted(true);

    const bullionValue = role === 'money' ? null : formData.bullionId || role;
    const payload = {
      name: formData.planName,
      bullion: bullionValue,
      minDuration: parseInt(formData.minDuration),
      maxDuration: parseInt(formData.maxDuration),
      durationType: 'months',
      isMoneyType: formData.isMoneyType,
      isWeightType: formData.isWeightType,
      ...(formData.isMoneyType && {
        minQty: Number(Number(formData.minQty).toFixed(2)),
        maxQty: Number(Number(formData.maxQty).toFixed(2)),
      }),
      ...(formData.isWeightType && {
        minGram: Number(Number(formData.minGram).toFixed(decimal)),
        maxGram: Number(Number(formData.maxGram).toFixed(decimal)),
      }),
      maxSipLimit: parseInt(formData.maxSipLimit),
      qtyUnit: 'Rs',
      benefits: benefitsList,
      enabled: true,
      comment: formData.disclaimer,
      percentage: formData.percentage ? parseFloat(formData.percentage) : 0,
      subsequent_percentage: formData.subsequent_percentage
        ? parseFloat(formData.subsequent_percentage)
        : 0,
      offerType: formData.offerType,

      interval: formData.interval,
      autodebit: formData.autodebit,
      ...(!formData.autodebit &&
        formData.installmentDuration !== '' &&
        formData.installmentDuration != null && {
          installmentDuration: Number(formData.installmentDuration),
        }),
      ...(autoDebitDateSelectionEnabled && {
        auto_debit_date_selection: Boolean(formData.auto_debit_date_selection),
      }),
      autoGroupCreation: sipAutoGroupCreationEnabled
        ? (formData.autoGroupCreation ?? false)
        : false,
      ...(formData.maxSipGroup !== '' &&
        formData.maxSipGroup != null &&
        sipAutoGroupCreationEnabled &&
        (formData.autoGroupCreation ?? false) && {
          maxSipGroup: parseInt(formData.maxSipGroup, 10),
        }),
      luckyDrawInterval: formData.luckyDrawInterval || null,
      luckyDrawDate:
        formData.luckyDrawDate !== undefined ? formData.luckyDrawDate : null,
      luckyDrawTime:
        formData.hour && formData.amPm
          ? formData.amPm === 'PM'
            ? (Number(formData.hour) % 12) + 12
            : Number(formData.hour) % 12
          : null,
      enableLuckyDraw: formData.enableLuckyDraw || false,
      multi_winner_lucky_draw:
        sipMultiWinnerLuckyDrawEnabled &&
        Boolean(formData.multi_winner_lucky_draw),
      no_of_winners:
        sipMultiWinnerLuckyDrawEnabled && formData.multi_winner_lucky_draw
          ? Math.max(2, parseInt(formData.no_of_winners, 10) || 2)
          : 1,
    };
    resetForm();
    dispatch(createSipPlanRequest(payload));
  };

  const resetForm = () => {
    setFormData({
      planName: '',
      bullionId: '',
      minDuration: '',
      maxDuration: '',
      minQty: '',
      maxQty: '',
      minGram: '',
      maxGram: '',
      isMoneyType: isOldAppVersion,
      isWeightType: false,
      maxSipLimit: '',
      benefits: '',
      disclaimer: '',
      percentage: '',
      subsequent_percentage: '',
      luckyDrawInterval: '',
      luckyDrawDate: '',
      hour: '',
      amPm: '',
      enableLuckyDraw: false,
      autoGroupCreation: false,
      maxSipGroup: '',
      multi_winner_lucky_draw: false,
      no_of_winners: '',
      auto_debit_date_selection: false,
      installmentDuration: '',
    });
    setRole('');
    setBenefitsList([]);
  };

  const handleClose = () => {
    setOpen(false);
    dispatch(resetSomethingState());
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (hasSubmitted && success) {
      setAlertData({ type: 'success', message: 'Created successfully!' });
      setOpen(true);
      resetForm();
      dispatch(resetSomethingState());
      setHasSubmitted(false);
    } else if (hasSubmitted && error) {
      setAlertData({ type: 'error', message: `Error: ${error}` });
      setOpen(true);
      dispatch(resetSomethingState());
      setHasSubmitted(false);
    }
  }, [success, error, dispatch, hasSubmitted]);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error('Invalid permissions JSON', e);
      }
    }
  }, []);
  const defaultMonths = [3, 6, 9, 12]; // fallback values

  const monthsToRender =
    monthOptions && monthOptions.length > 0 ? monthOptions : defaultMonths;
  const canCreateSip = canShowPermission(
    permissions,
    'createSipPlan',
    'create',
  );
  const canEdit = canShowPermission(permissions, 'createSipPlan', 'edit');
  const canDelete = canShowPermission(permissions, 'createSipPlan', 'delete');

  const columns = [
    { field: 'id', headerName: 'Id', hide: true },
    { field: 'name', headerName: 'Name' },
    {
      field: 'durationType',
      headerName: 'Plan Type',
      render: (val, row) => {
        return row.bullion === null ? 'Money' : row.bullion.metalName || '';
      },
    },
    { field: 'minDuration', headerName: 'Min Month' },
    { field: 'maxDuration', headerName: 'Max Month' },
    {
      field: 'minQty',
      headerName: 'Min Amount',
      render: (value, row) =>
        row?.isMoneyType
        ? value != null && value !== ''
          ? Number(value).toFixed(2)
          : '-'
        : 'N.A.',
    },
    {
      field: 'maxQty',
      headerName: 'Max Amount',
      render: (value, row) => row?.isMoneyType
        ? value != null && value !== ''
          ? Number(value).toFixed(2)
          : '-'
        : 'N.A.',
    },
    {
      field: 'minGram',
      headerName: 'Min Gram',
      render: (value, row) => row?.isWeightType
        ? value != null && value !== ''
          ? Number(value).toFixed(decimal)
          : '-'
        : 'N.A.',
    },
    {
      field: 'maxGram',
      headerName: 'Max Gram',
      render: (value, row) => row?.isWeightType
        ? value != null && value !== ''
          ? Number(value).toFixed(decimal)
          : '-'
        : 'N.A.',
    },
    {
      field: 'benefits',
      headerName: 'Benefits',
      render: (value) => (Array.isArray(value) ? value.join(', ') : '-'),
    },
    {
      field: 'createdAt',
      headerName: 'Applicable From',
      minWidth: 250,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date.getTime())
          ? 'Invalid Date'
          : new Intl.DateTimeFormat('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }).format(date);
      },
    },
    { field: 'qtyUnit', headerName: 'Unit' },
    {
      field: 'maxSipLimit',
      headerName: 'Max Customer Limit',
    },
    {
      field: 'autoGroupCreation',
      headerName: 'Auto Group Creation',
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      field: 'maxSipGroup',
      headerName: 'Max SIP Group',
      render: (value, row) => {
        if (!row?.autoGroupCreation) return 'N.A.';
        return value === 0 || value ? value : '-';
      },
    },
    {
      field: 'groupNumber',
      headerName: 'Group No',
      render: (value, row) => {
        if (!row?.autoGroupCreation) return 'N.A.';
        return value === 0 || value ? value : '-';
      },
    },
    {
      field: 'parentPlanId',
      headerName: 'Parent SIP',
      minWidth: 160,
      render: (value, row) => {
        if (!row?.autoGroupCreation) return 'N.A.';
        // If there's no parentPlanId, this is the root/parent plan in the chain
        if (!value)
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                gap: 1,
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                py: 0,
              }}
            >
              <Typography variant="body2" component="span">
                Parent
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  px: 1.25,
                  py: 0.5,
                  fontSize: 12,
                  lineHeight: 1.1,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  backgroundColor: '#7028DA',
                  boxShadow: 'none',
                  textDecoration: 'underline',
                  textDecorationColor: '#1976d2',
                  textUnderlineOffset: '3px',
                  '&:hover': {
                    backgroundColor: '#5f21bd',
                    boxShadow: 'none',
                  },
                }}
                onClick={() => navigate(`/SipPlanGroup/${row?._id}`)}
              >
                View Plans
              </Button>
            </Box>
          );
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" component="span">
              Child
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'percentage',
      headerName: 'Percentage',
    },
    {
      field: 'subsequent_percentage',
      headerName: 'Subsequent Percentage',
    },
    {
      field: 'offerType',
      headerName: 'Offer Type',
      width: 150,
      render: (value, row) => row?.offerType || 'N/A',
    },
    { field: 'interval', headerName: 'Auto Debit Interval' },
    {
      field: 'luckyspin',
      headerName: 'Lucky Spin',
      render: (val, row) => {
        // const handleLuckySpinClick = () => {
        //   dispatch(fetchAllSipListRequest({ id: row._id }));
        //   //navigate("/SipPlan");
        // };
        if (!row.enableLuckyDraw) return null;
        return canEdit ? (
          <button
            onClick={() => handleLuckySpinClick(row)}
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Lucky Draw
          </button>
        ) : null;
      },
    },
    {
      field: '_id-viewSips',
      headerName: 'View Sip',
      render: (val, row) => {
        const handleClick = () => {
          navigate(`/SipSubscriberList?id=${row._id}`);
        };

        return (
          <button
            onClick={handleClick}
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            View
          </button>
        );
      },
    },
    ...(canEdit
      ? [
          {
            field: '_id-edit',
            headerName: 'Edit',
            render: (value, row) => (
              <CiEdit
                style={{ cursor: 'pointer' }}
                onClick={() => handleEditClick(row)}
              />
            ),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            field: '_id-delete',
            headerName: 'Delete',
            render: (value, row) => (
              <button
                onClick={() => handleDeleteClick(row._id)}
                style={{
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            ),
          },
        ]
      : []),
    {
      field: 'enabled',
      headerName: 'Status',
      render: (value, row) =>
        canEdit ? (
          <button
            onClick={() => handleStatusChange(row._id, row.enabled)}
            style={{
              backgroundColor: row.enabled ? 'green' : '#7028DA',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              textTransform: 'none',
              cursor: 'pointer',
            }}
          >
            {row.enabled ? 'Enabled' : 'Disabled'}
          </button>
        ) : (
          <span style={{ fontWeight: 600 }}>
            {row.enabled ? 'Enabled' : 'Disabled'}
          </span>
        ),
    },
    {
      field: 'runningSipCount',
      headerName: 'Running SIPs',
      width: 150,
      hideInExcel: true,
      render: (value, row) => {
        if (row?.commonSips && Array.isArray(row.commonSips)) {
          const count = row.commonSips.filter(
            (sip) => sip.status === 'RUNNING',
          ).length;

          return count > 0 ? count : '0';
        }
        return '0';
      },
    },
    {
      field: 'completedSipCount',
      headerName: 'Completed SIPs',
      width: 150,
      hideInExcel: true,
      render: (value, row) => {
        if (row?.commonSips && Array.isArray(row.commonSips)) {
          const count = row.commonSips.filter(
            (sip) => sip.status === 'COMPLETED',
          ).length;

          return count > 0 ? count : '0';
        }
        return '0';
      },
    },
    {
      field: 'createdBY',
      headerName: 'Created By',
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.createdBy ? row.createdBy.name : 'N/A';
      },
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.updatedBy ? row.updatedBy.name : 'N/A';
      },
    },
  ];
  const weeklyDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const monthlyDays = Array.from({ length: 31 }, (_, i) => i + 1); // 1 to 28
  const hours = Array.from({ length: 12 }, (_, i) => i + 1); // 1 to 12
  const amPm = ['AM', 'PM'];
  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log('Store CSV API called');

    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/moneysip/sipPlanCsv',
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, 'sipPlanCsv.csv');
        },
      }),
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100vh' }}>
      <Box
        sx={{
          width: '100%',
          mt: 0,
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ justifyContent: 'space-between', display: 'flex', p: 2 }}>
          <Typography
            fontFamily={'Aeonik'}
            sx={{ fontSize: '24px', fontWeight: 700 }}
          >
            Sip List
          </Typography>
          {canCreateSip && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: 'none',
                background: '#7028DA',
                fontFamily: 'inherit',
              }}
              onClick={() => setOpenCreateDialog(true)}
            >
              Create New Plan{' '}
            </Button>
          )}
        </Box>
        <CardContent
          sx={{
            height: '580px',
            width: '100%',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableCompo
              columns={columns}
              data={sipPlans}
              totalCount={total}
              stickyColumnFields={['name']}
              onTableChange={handleTableChange}
              page={pagination.page}
              limit={pagination.limit}
              extraButtons={
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={handleDownloadStoreCsv}
                >
                  Download CSV
                </Button>
              }
            />
          )}
        </CardContent>
      </Box>
      {/* lucky spin model */}
      <LuckySpinModal
        isOpen={isSpinModalOpen}
        onClose={handleCloseModal}
        plan={selectedSipPlan ? selectedSipPlan : {}} // Pass the context ID
      />

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit SIP Plan</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Plan Type"
            fullWidth
            value={formData.durationType || ''}
            onChange={(e) =>
              setFormData({ ...formData, durationType: e.target.value })
            }
          />
          {/* Min Duration */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Min Duration (Months)</InputLabel>
            <Select
              value={formData.minDuration || ''}
              onChange={(e) =>
                setFormData({ ...formData, minDuration: e.target.value })
              }
            >
              {monthsToRender.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Max Duration (Months)</InputLabel>
            <Select
              value={formData.maxDuration || ''}
              onChange={(e) =>
                setFormData({ ...formData, maxDuration: e.target.value })
              }
            >
              {monthsToRender.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!formData.autodebit && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Installment Duration</InputLabel>
              <Select
                name="installmentDuration"
                value={formData.installmentDuration ?? ''}
                label="Installment Duration"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    installmentDuration: e.target.value,
                  })
                }
              >
                {installmentDurationOptions.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Type Selection: Money / Weight */}
          {!isOldAppVersion && (
          <Grid item size={12}>
            <Typography
              variant="subtitle1"
              sx={{ color: '#606778', mb: 1.5, fontWeight: 600 }}
            >
              Plan Value Type <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                {
                  key: 'isMoneyType',
                  label: 'Amount',
                  icon: <CurrencyRupeeIcon fontSize="small" />,
                },
                {
                  key: 'isWeightType',
                  label: 'Weight',
                  icon: <MonitorWeightIcon fontSize="small" />,
                },
              ].map((opt) => {
                const checked = Boolean(formData[opt.key]);
                return (
                  <Box
                    key={opt.key}
                    onClick={() =>
                      handleTypeChange({
                        target: { name: opt.key, checked: !checked },
                      })
                    }
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: '10px 14px',
                      border: '1.5px solid',
                      borderColor: checked ? '#7028DA' : '#E2E2EA',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: checked ? 'rgba(112, 40, 218, 0.06)' : '#fff',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        borderColor: '#7028DA',
                        background: 'rgba(112, 40, 218, 0.04)',
                      },
                    }}
                  >
                    <Checkbox
                      name={opt.key}
                      checked={checked}
                      onChange={handleTypeChange}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        p: 0.5,
                        color: '#B7BAC6',
                        '&.Mui-checked': { color: '#7028DA' },
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: checked ? '#7028DA' : '#F1F1F5',
                        color: checked ? '#fff' : '#8A8A9A',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      {opt.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: checked ? '#7028DA' : '#33333F',
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          )}
          {/* Min/Max Amount - shown only when Money is checked */}
          {formData.isMoneyType && (
            <>
              <Grid item size={6} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Minimum Quantity <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Quantity"
                  size="small"
                  name="minQty"
                  value={formData.minQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
              <Grid item size={6} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Maximum Quantity <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Quantity"
                  size="small"
                  name="maxQty"
                  value={formData.maxQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Min/Max Gram - shown only when Weight is checked */}
          {formData.isWeightType && (
            <>
              <Grid item size={6} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Minimum Gram <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Gram"
                  size="small"
                  name="minGram"
                  value={formData.minGram}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,3}$/.test(value) || value === '') {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
              <Grid item size={6} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Maximum Gram <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Gram"
                  size="small"
                  name="maxGram"
                  value={formData.maxGram}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,3}$/.test(value) || value === '') {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
            </>
          )}
          <TextField
            margin="dense"
            label="Benefits (comma separated)"
            fullWidth
            value={
              (Array.isArray(formData.benefits)
                ? formData.benefits.join(', ')
                : formData.benefits) || ''
            }
            onChange={(e) =>
              setFormData({
                ...formData,
                benefits: e.target.value.split(',').map((item) => item.trim()),
              })
            }
          />
          <TextField
            margin="dense"
            label="Qty Unit"
            fullWidth
            value={formData.qtyUnit || ''}
            onChange={(e) =>
              setFormData({ ...formData, qtyUnit: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Max SIP Limit"
            type="number"
            fullWidth
            value={formData.maxSipLimit || ''}
            onChange={(e) =>
              setFormData({ ...formData, maxSipLimit: Number(e.target.value) })
            }
          />
          {autoDebitDateSelectionEnabled && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  name="auto_debit_date_selection"
                  checked={Boolean(formData.auto_debit_date_selection)}
                  onChange={handleChange}
                />
              }
              label="Auto Debit Date Selection"
            />
          )}
          {/* SIP Lucky Draw Section */}
          {sipLuckyDrawStatus && (
            <Box mt={2}>
              <Typography variant="subtitle1" sx={{ color: '#606778', mb: 2 }}>
                SIP Lucky Draw Configuration
              </Typography>
              <Grid container spacing={2} mt={1}>
                {/* Interval */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                      name="luckyDrawInterval"
                      label="luckyDrawInterval"
                      value={formData.luckyDrawInterval || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          luckyDrawInterval: e.target.value,
                          luckyDrawDate: '',
                          hour: '',
                          amPm: '',
                        })
                      }
                    >
                      <MenuItem value="WEEKLY">Weekly</MenuItem>
                      <MenuItem value="MONTHLY">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Date</InputLabel>
                    <Select
                      name="luckyDrawDate"
                      label="luckyDrawDate"
                      value={
                        formData.luckyDrawInterval === 'WEEKLY'
                          ? weeklyDays[formData.luckyDrawDate] || ''
                          : formData.luckyDrawDate || ''
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        if (formData.luckyDrawInterval === 'WEEKLY') {
                          const storedValue = weeklyDays.indexOf(value);
                          setFormData({
                            ...formData,
                            luckyDrawDate: storedValue,
                          });
                        } else {
                          setFormData({
                            ...formData,
                            luckyDrawDate: parseInt(value),
                          });
                        }
                      }}
                    >
                      {(formData.luckyDrawInterval === 'WEEKLY'
                        ? weeklyDays
                        : monthlyDays
                      ).map((d, idx) => (
                        <MenuItem key={idx} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Hour */}
                <Grid item xs={3}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Time</InputLabel>
                    <Select
                      name="hour"
                      label="hour"
                      value={formData.hour || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, hour: e.target.value })
                      }
                    >
                      {hours.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* AM/PM */}
                <Grid item xs={3}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>AM/PM</InputLabel>
                    <Select
                      name="amPm"
                      label="amPm"
                      value={formData.amPm || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, amPm: e.target.value })
                      }
                    >
                      {amPm.map((ap) => (
                        <MenuItem key={ap} value={ap}>
                          {ap}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* Enable/Disable Lucky Draw Button */}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <InputLabel sx={{ minWidth: 150 }}>
                      Enable Lucky Draw:
                    </InputLabel>
                    <Button
                      variant={
                        formData.enableLuckyDraw ? 'contained' : 'outlined'
                      }
                      color={formData.enableLuckyDraw ? 'success' : 'error'}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          enableLuckyDraw: !prev.enableLuckyDraw,
                        }))
                      }
                    >
                      {formData.enableLuckyDraw ? 'Enabled' : 'Disabled'}
                    </Button>
                  </Box>
                </Grid>
                {sipMultiWinnerLuckyDrawEnabled && (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(formData.multi_winner_lucky_draw)}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                multi_winner_lucky_draw: e.target.checked,
                                no_of_winners: e.target.checked
                                  ? prev.no_of_winners || 2
                                  : '',
                              }))
                            }
                          />
                        }
                        label="Multi Winner Lucky Draw"
                      />
                    </Grid>
                    {formData.multi_winner_lucky_draw && (
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Number of winners per draw"
                          name="no_of_winners"
                          inputProps={{ min: 2 }}
                          value={formData.no_of_winners}
                          onChange={handleChange}
                          helperText={getMultiWinnerHelperText(
                            formData.no_of_winners,
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const luckyDrawTime =
                formData.hour && formData.amPm
                  ? formData.amPm === 'PM'
                    ? (Number(formData.hour) % 12) + 12
                    : Number(formData.hour) % 12
                  : null;

              const { installmentDuration, ...planFields } = formData;
              dispatch(
                createSipPlanRequest({
                  ...planFields,
                  name: getEditPayloadPlanName(
                    formData.name,
                    formData.autoGroupCreation === true,
                  ),
                  isMoneyType: formData.isMoneyType,
                  isWeightType: formData.isWeightType,
                  luckyDrawTime,
                  enableLuckyDraw: formData.enableLuckyDraw,
                  multi_winner_lucky_draw:
                    sipMultiWinnerLuckyDrawEnabled &&
                    Boolean(formData.multi_winner_lucky_draw),
                  no_of_winners:
                    sipMultiWinnerLuckyDrawEnabled &&
                    formData.multi_winner_lucky_draw
                      ? Math.max(2, parseInt(formData.no_of_winners, 10) || 2)
                      : 1,
                  ...(autoDebitDateSelectionEnabled && {
                    auto_debit_date_selection: Boolean(
                      formData.auto_debit_date_selection,
                    ),
                  }),
                  ...(!formData.autodebit &&
                    installmentDuration !== '' &&
                    installmentDuration != null && {
                      installmentDuration: Number(installmentDuration),
                    }),
                }),
              );
              setOpenEditDialog(false);
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '3px',
          },
        }}
      >
        <Box sx={{ overflowY: 'auto' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              cursor: 'pointer',
              background: '#E2E2EA',
              borderRadius: '50%',
              width: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#646464',
            }}
            onClick={() => setOpenCreateDialog(false)}
          >
            <CloseIcon />
          </Box>
        </Box>

        <Typography
          fontWeight={700}
          fontSize={24}
          p={2}
          mb={3}
          fontFamily={'Aeonik'}
          sx={{
            background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
            color: '#fff',
          }}
        >
          Create SIP Plan
        </Typography>

        <Grid container spacing={2} m={2} P={2}>
          {/* Column 1 */}
          <Grid item size={6}>
            {/* Plan Name */}
            <TextField
              fullWidth
              label={
                <>
                  Plan Name <span style={{ color: 'red' }}>*</span>
                </>
              }
              placeholder="Enter Plan Name"
              size="small"
              name="planName"
              value={formData.planName}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                  handleChange(e);
                }
              }}
            />
          </Grid>
          <Grid item size={6}>
            {' '}
            {/* Metal Select */}
            <FormControl fullWidth size="small">
              <InputLabel id="metal-label">
                Select Metal <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <Select
                labelId="metal-label"
                value={formData.role}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    role: value,
                    isMoneyType: value === 'money' ? true : prev.isMoneyType,
                    isWeightType: value === 'money' ? false : prev.isWeightType,
                  }));
                }}
                label="Select Metal"
              >
                <MenuItem value="" disabled>
                  <em>Select Metal</em>
                </MenuItem>
                <MenuItem value="money">Money</MenuItem>
                {enabledMetals
                  .filter((metal) => metal.status === true)
                  .map((metal) => (
                    <MenuItem key={metal._id} value={metal._id}>
                      {`${metal.metalName} - ${metal.karat}K - ${metal.purity}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item size={6}>
            {' '}
            {/* Min Duration */}
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>
                  Minimum Duration <span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  name="minDuration"
                  // sx={{ width: "300px" }}
                  value={formData.minDuration}
                  onChange={handleChange}
                  label="Minimum Duration"
                >
                  <MenuItem value="">
                    <em>Select duration</em>
                  </MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month} Months
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>{' '}
          </Grid>

          <Grid item size={6}>
            {' '}
            {/* Max Duration */}
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>
                  Maximum Duration <span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  name="maxDuration"
                  value={formData.maxDuration}
                  onChange={handleChange}
                  label="Maximum Duration"
                >
                  <MenuItem value="">Select duration</MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month} Months
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          {/* Type Selection: Money / Weight */}
          {!isOldAppVersion && (
          <Grid item size={12}>
            <Typography
              variant="subtitle1"
              sx={{ color: '#606778', mb: 1.5, fontWeight: 600 }}
            >
              Plan Value Type <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                {
                  key: 'isMoneyType',
                  label: 'Amount',
                  icon: <CurrencyRupeeIcon fontSize="small" />,
                },
                {
                  key: 'isWeightType',
                  label: 'Weight',
                  icon: <MonitorWeightIcon fontSize="small" />,
                },
              ].map((opt) => {
                const checked = Boolean(formData[opt.key]);
                const isDisabled =
                  opt.key === 'isWeightType' && formData.role === 'money';
                return (
                  <Box
                    key={opt.key}
                    onClick={() => {
                      if (isDisabled) return;
                      handleTypeChange({
                        target: { name: opt.key, checked: !checked },
                      });
                    }}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: '10px 14px',
                      border: '1.5px solid',
                      borderColor: checked ? '#7028DA' : '#E2E2EA',
                      borderRadius: '10px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      opacity: isDisabled ? 0.5 : 1,
                      background: checked ? 'rgba(112, 40, 218, 0.06)' : '#fff',
                      transition: 'all 0.18s ease',
                      '&:hover': isDisabled
                        ? {}
                        : {
                            borderColor: '#7028DA',
                            background: 'rgba(112, 40, 218, 0.04)',
                          },
                    }}
                  >
                    <Checkbox
                      name={opt.key}
                      checked={checked}
                      disabled={isDisabled}
                      onChange={handleTypeChange}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        p: 0.5,
                        color: '#B7BAC6',
                        '&.Mui-checked': { color: '#7028DA' },
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: checked ? '#7028DA' : '#F1F1F5',
                        color: checked ? '#fff' : '#8A8A9A',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      {opt.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: checked ? '#7028DA' : '#33333F',
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          )}
          {/* Min/Max Amount - shown only when Money is checked */}
          {formData.isMoneyType && (
            <>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Minimum Quantity <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Quantity"
                  size="small"
                  name="minQty"
                  value={formData.minQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Maximum Quantity <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Quantity"
                  size="small"
                  name="maxQty"
                  value={formData.maxQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Min/Max Gram - shown only when Weight is checked */}
          {formData.isWeightType && (
            <>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Minimum Gram <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Gram"
                  size="small"
                  name="minGram"
                  value={formData.minGram}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,3}$/.test(value) || value === '') {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Maximum Gram <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Add Gram"
                  size="small"
                  name="maxGram"
                  value={formData.maxGram}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,3}$/.test(value) || value === '') {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Offer Type */}
          {sipOfferType && (
            <Grid item size={6}>
              {' '}
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Offer Type</InputLabel>
                  <Select
                    name="offerType"
                    label="offerType"
                    value={formData.offerType}
                    onChange={handleChange}
                  >
                    {/* <MenuItem value="">
                    <em>Select Offer Type</em>
                  </MenuItem>   */}
                    <MenuItem value="Normal">Normal</MenuItem>
                    <MenuItem value="PREPAID_FIRST_INSTALLMENT">
                      Prepaid First Installment
                    </MenuItem>
                    <MenuItem value="EVERY_INSTALLMENT_DISCOUNT">
                      Every Installment Discount
                    </MenuItem>

                    <MenuItem value="FIRST_AND_SUBSEQUENT">
                      Offer for First & Subsequent
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          )}

          <Grid item size={6}>
            {' '}
            <Box>
              <TextField
                fullWidth
                label="Percentage (%)"
                name="percentage"
                type="number"
                placeholder="Enter percentage"
                size="small"
                value={formData.percentage}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleChange(e);
                  }
                }}
                disabled={
                  ![
                    'EVERY_INSTALLMENT_DISCOUNT',
                    'FIRST_AND_SUBSEQUENT',
                  ].includes(formData.offerType)
                }
              />
            </Box>
          </Grid>

          <Grid item size={6}>
            {' '}
            <Box>
              <TextField
                fullWidth
                label="Subsequent Percentage"
                type="number"
                size="small"
                value={formData.subsequent_percentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    subsequent_percentage: e.target.value,
                  })
                }
                disabled={formData.offerType !== 'FIRST_AND_SUBSEQUENT'}
              />
            </Box>
          </Grid>
          <Grid item size={6}>
            {' '}
            <Box>
              <TextField
                fullWidth
                label={
                  <>
                    Max Customer Limit <span style={{ color: 'red' }}>*</span>
                  </>
                }
                placeholder="Enter max customer limit for that plan"
                size="small"
                name="maxSipLimit"
                value={formData.maxSipLimit}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleChange(e);
                  }
                }}
              />
            </Box>{' '}
          </Grid>
        
          {sipAutoGroupCreationEnabled && (
            <>
              <Grid item size={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="autoGroupCreation"
                      checked={formData.autoGroupCreation ?? false}
                      onChange={handleChange}
                    />
                  }
                  label="Auto Group Creation"
                />
              </Grid>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label="Max SIP Group creation limit"
                  type="number"
                  placeholder="Enter max SIP group creation limit"
                  size="small"
                  name="maxSipGroup"
                  value={formData.maxSipGroup}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                />
              </Grid>
            </>
          )}
          {!formData.autodebit && (
            <Grid item size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Installment Duration</InputLabel>
                <Select
                  name="installmentDuration"
                  value={formData.installmentDuration ?? ''}
                  onChange={handleChange}
                  label="Installment Duration"
                >
                  {installmentDurationOptions.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {sipLuckyDrawStatus && (
            <Box mt={1}>
              <Typography variant="subtitle1" sx={{ color: '#606778', mb: 2 }}>
                SIP Lucky Draw Configuration
              </Typography>
              <Grid container spacing={2}>
                {/* Interval Select */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                      name="luckyDrawInterval"
                      label="luckyDrawInterval"
                      value={formData.luckyDrawInterval || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          luckyDrawInterval: e.target.value,
                          date: '',
                          hour: '',
                          amPm: '',
                        })
                      }
                    >
                      <MenuItem value="WEEKLY">Weekly</MenuItem>
                      <MenuItem value="MONTHLY">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Select */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Date</InputLabel>
                    <Select
                      name="luckyDrawDate"
                      label="luckyDrawDate"
                      value={
                        formData.luckyDrawInterval === 'WEEKLY'
                          ? weeklyDays[formData.luckyDrawDate] || ''
                          : formData.luckyDrawDate || ''
                      }
                      onChange={(e) => {
                        let value = e.target.value;
                        if (formData.luckyDrawInterval === 'WEEKLY') {
                          // store 0-6
                          const storedValue = weeklyDays.indexOf(value);
                          setFormData({
                            ...formData,
                            luckyDrawDate: storedValue,
                          });
                        } else {
                          setFormData({
                            ...formData,
                            luckyDrawDate: parseInt(value),
                          });
                        }
                      }}
                    >
                      {(formData.luckyDrawInterval === 'WEEKLY'
                        ? weeklyDays
                        : monthlyDays
                      ).map((d, idx) => (
                        <MenuItem key={idx} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Hour Select */}
                <Grid item xs={3}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Time</InputLabel>
                    <Select
                      name="hour"
                      label="hour"
                      value={formData.hour || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, hour: e.target.value })
                      }
                    >
                      {hours.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* AM/PM Select */}
                <Grid item xs={3}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>AM/PM</InputLabel>
                    <Select
                      name="amPm"
                      label="amPm"
                      value={formData.amPm || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, amPm: e.target.value })
                      }
                    >
                      {amPm.map((ap) => (
                        <MenuItem key={ap} value={ap}>
                          {ap}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {sipMultiWinnerLuckyDrawEnabled && (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(formData.multi_winner_lucky_draw)}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                multi_winner_lucky_draw: e.target.checked,
                                no_of_winners: e.target.checked
                                  ? prev.no_of_winners || 2
                                  : '',
                              }))
                            }
                          />
                        }
                        label="Multi Winner Lucky Draw"
                      />
                    </Grid>
                    {formData.multi_winner_lucky_draw && (
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Number of winners per draw"
                          name="no_of_winners"
                          inputProps={{ min: 2 }}
                          value={formData.no_of_winners}
                          onChange={handleChange}
                          helperText={getMultiWinnerHelperText(
                            formData.no_of_winners,
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}

          <Grid item size={12}>
            {' '}
            <FormControl
              fullWidth
              sx={{
                width: '100%', // ensures full width
                minWidth: { xs: '210px', sm: '420px' }, // optional
              }}
            >
              <Typography variant="subtitle1" sx={{ color: '#606778', mb: 0 }}>
                Plan Disclaimer
              </Typography>
              <TextareaAutosize
                minRows={7}
                sx={{
                  width: '100%', // ensures full width

                  padding: 10,
                  borderRadius: 6,
                  borderColor: '#ccc',
                }}
                placeholder="Enter Plan Disclaimer"
                name="disclaimer"
                value={formData.disclaimer}
                onChange={handleChange}
              />
            </FormControl>{' '}
          </Grid>
          {/* Enable Auto Debit Checkbox */}
          {/* <Grid item size={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="autodebit"
                  checked={formData.autodebit}
                  onChange={handleChange}
                />
              }
              label="Enable Auto Debit"
            />
          </Grid> */}
          {getAppConfigData?.featureToggle?.sip_auto_debit && (
            <Grid item size={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="autodebit"
                    checked={formData.autodebit}
                    onChange={handleChange}
                  />
                }
                label="Enable Auto Debit"
              />
            </Grid>
          )}
          {autoDebitDateSelectionEnabled && (
            <Grid item size={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="auto_debit_date_selection"
                    checked={Boolean(formData.auto_debit_date_selection)}
                    onChange={handleChange}
                  />
                }
                label="Auto Debit Date Selection"
              />
            </Grid>
          )}

          {/* Show Auto Debit Dropdown only if enabled */}
          {formData.autodebit && (
            <Grid item size={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>
                  Auto Debit Interval<span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  size="small"
                  name="interval"
                  value={formData.interval}
                  onChange={handleChange}
                  label="interval"
                >
                  <MenuItem value="DAY">Daily</MenuItem>
                  <MenuItem value="MONTH">Monthly</MenuItem>
                  <MenuItem value="WEEK">Weekly</MenuItem>
                  {/* <MenuItem value="WEEK">Yearly</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
          )}
          {/* Benefits */}
          <Grid item size={6}>
            {' '}
            <Box>
              <TextField
                fullWidth
                label="Benefit"
                name="benefits"
                placeholder="Enter benefit and click Add"
                value={formData.benefits}
                onChange={handleChange}
                size="small"
              />
            </Box>
          </Grid>
          {/* Add Button */}
          <Grid item size={6}>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ background: '#7028DA', textTransform: 'none' }}
                onClick={handleAddBenefit}
              >
                Add
              </Button>
            </Box>
          </Grid>

          {/* Added Benefits */}
          {benefitsList.length > 0 && (
            <Box mt={2}>
              <Typography fontWeight={600} fontSize={14}>
                Added Benefits:
              </Typography>
              <ul style={{ marginTop: 5, paddingLeft: 20 }}>
                {benefitsList.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </Box>
          )}
        </Grid>

        <DialogActions>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ backgroundColor: '#7028DA', textTransform: 'none', px: 6 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*  Status Confirmation Dialog */}
      <Dialog open={confirmStatusDialogOpen} onClose={handleConfirmStatusNo}>
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to change the status of this SIP plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmStatusYes}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmStatusNo}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/*  Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteDialogOpen} onClose={handleConfirmDeleteNo}>
        <DialogTitle>Delete SIP Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this SIP plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteYes}
          >
            Yes
          </Button>
          <Button variant="contained" onClick={handleConfirmDeleteNo}>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateSipPlanTable;
